import React from 'react';
import { z } from 'zod';

import type {
  Category,
  Checkbox,
} from '@rover/components/src/formFields/CollapsingCheckBoxCollection/CheckBoxCategory';
import { useKibbleForm } from '@rover/kibble/forms';
import CheckboxField from '@rover/kibble/forms/fields/CheckboxField';

const schema = z.object({ checkboxes: z.array(z.string()) });

const getOptionValue = (checkbox: Checkbox): string =>
  checkbox.value != null ? String(checkbox.value) : checkbox.attribute;

type Props = {
  categories: Category[];
  defaultChecked: string[];
  onChange?: (
    updatedCheckboxFilter: Record<string, boolean>,
    checkboxValue: string | null | undefined
  ) => void;
};

type CheckboxFieldOnChange = React.ComponentProps<typeof CheckboxField>['onChange'];

const CheckboxCollection = ({ categories, defaultChecked, onChange }: Props) => {
  const { configureField } = useKibbleForm({
    schema,
    defaultValues: { checkboxes: defaultChecked },
    values: { checkboxes: defaultChecked },
  });

  return categories.map((category) => {
    const handleChange: CheckboxFieldOnChange = (e) => {
      if (!e.webEvent) return;
      const { value, checked } = e.webEvent.target;
      const checkbox = category.checkboxes.find((cb) => getOptionValue(cb) === value);
      if (checkbox) {
        onChange?.({ [checkbox.attribute]: checked }, checkbox.value ?? value);
      }
    };

    return (
      <CheckboxField
        key={category.category}
        {...configureField('checkboxes')}
        primaryLabel={category.category}
        required={false}
        options={category.checkboxes.map((checkbox) => ({
          primaryLabel: checkbox.label,
          value: getOptionValue(checkbox),
        }))}
        controlSide="right"
        onChange={handleChange}
      />
    );
  });
};

export default CheckboxCollection;
